import React, { FunctionComponent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import RestoreIcon from '@mui/icons-material/Restore';

import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
} from '@mui/material';

import {
  Button,
  Card,
  NumberInput,
  Page,
  Select,
  Switcher,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { SnackSaved } from '../../../components/v2/snacks/Saved';
import { Confirm } from '../../../components/v2/dialogs/Confirm';

import { fetch_all, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import HelpersLanguage from '../../../actions/helpers/language';
import HelpersPractice from '../../../actions/helpers/practice';

import { AdhProfile } from '../../../models/AdhProfile';
import { AdhLink } from '../../../models/AdhLink';
import { User } from '../../../models/User';

const { DateTime } = require("luxon");

type Props = {}

const fetchClient = (login: any, i18n: any, id: number) => {
  return fetch_one<User>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'user',
      id: id,
      fields: ['id', 'first_name', 'last_name', 'call_name', 'birth_date', 'gender', 'adh_profile_id', 'calculated_adh_profile_id', 'consents'],
      sub_fields: {
        consents: ['id', 'practice_id', 'enabled', 'adh_profile_id']
      },
      only_one: 1
    },
    login
  );
}

const fetchProfile = (login: any, i18n: any, id: number) => {
  return fetch_one<AdhProfile>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'adh_profile',
      fields: ['id', 'name', 'full_name', 'description', 'full_description', 'source', 'source_date', 'age_low', 'age_high', 'gender', 'is_pregnant', 'adh_links', 'derived_id', 'derived'],
      sub_fields: {
        adh_links: ['id', 'nutritional_value', 'nutritional_value_id', 'lower_limit', 'higher_limit', 'maximum', 'is_tracked_client', 'measure_mode'],
        nutritional_value: ['id', 'name', 'is_grams_per_kilogram', 'measure', 'is_client_enabled', 'weight'],
        derived: ['id', 'name', 'full_name']
      },
      id: id
    },
    login
  );
}

const fetchAvailableProfiles = (login: any, i18n: any) => {
  return fetch_all<AdhProfile>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'adh_profile',
      fields: ['id', 'name', 'full_name', 'description', 'full_description', 'age_low', 'age_high', 'gender', 'is_pregnant', 'country'],
      sub_fields: {
        country: ['id', 'name']
      },
      order: "name ASC",
      per_page: 100,
      page: 0,
      filter: {
        search: '',
        advanced: {
          no_practice: '1',
          no_user: '1'
        }
      }
    },
    login
  );
}

const selectContentWithId = (field?: {[z:string]: string}, extraFallback?: string) => {
  let correct = (field || {})[HelpersLanguage.mapping_to_id(localStorage.getItem('i18nextLng') || 'nl')];
  return correct || (field || {})['1'] || (field || {})['nl'] || Object.values(field || {}).filter(fff => !!fff)[0] || extraFallback || '';
}

const limitUnit = (ll: AdhLink) => {
  if (ll.measure_mode === 1) return "g/kg";
  if (ll.measure_mode === 2) return "%EN";
  return ll.nutritional_value?.measure || '';
}

const formatNumber = (n?: number) => {
  return parseFloat((n || 0).toString()).toLocaleString('nl-BE', {maximumFractionDigits: 1});
}

const compareLinks = (a: AdhLink, b: AdhLink) => {
  const aw = a.nutritional_value?.weight ?? -1;
  const bw = b.nutritional_value?.weight ?? -1;
  if (aw !== bw) return bw - aw;
  return (a.nutritional_value?.name || '').localeCompare(b.nutritional_value?.name || '');
}

const describeAge = (profile: AdhProfile) => {
  if (!!profile.age_low && !!profile.age_high) return `${profile.age_low} - ${profile.age_high}`;
  if (!!profile.age_low) return `${profile.age_low}+`;
  if (!!profile.age_high) return `<= ${profile.age_high}`;
  return undefined;
}

const describeProfile = (profile: AdhProfile, t: any) => {
  let parts = [
    describeAge(profile),
    (profile.gender === 1 ? t("client.calculation.gender.male") : profile.gender === 0 ? t("client.calculation.gender.female") : undefined),
    (!!profile.is_pregnant ? t("client.calculation.gender.pregnant") : undefined)
  ].filter(pp => !!pp);
  return parts.join(" / ");
}

export const CoachUserCalculation: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const {id} = useParams();
  const {t, i18n} = useTranslation(['translations']);
  const queryClient = useQueryClient();

  const is_calculated_diary = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'calculated_diary'});

  const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>(undefined);
  const [allowClientEdit, setAllowClientEdit] = useState<boolean>(false);
  const [draftLinks, setDraftLinks] = useState<AdhLink[] | undefined>(undefined);
  const [draftName, setDraftName] = useState<string | undefined>(undefined);
  const [searchLink, setSearchLink] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState<boolean>(false);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);

  const {
    data: client,
    isLoading: clientLoading,
  } = useQuery({
    queryKey: ["coach", "calculation_client", id],
    queryFn: () => fetchClient(login, i18n, parseInt(id || '', 10)),
    enabled: !!login?.id && parseInt(id || '', 10) > 0 && !!is_calculated_diary,
  });

  const consentAdhId = ((client?.consents || [] as any[]).find((ccc: any) => !!ccc.enabled && !!ccc.adh_profile_id) || {} as any).adh_profile_id;
  const activeAdhId = consentAdhId || client?.adh_profile_id || client?.calculated_adh_profile_id;
  const isOwnChoice = !consentAdhId && !!client?.adh_profile_id;

  const {
    data: profile,
    isLoading: profileLoading,
  } = useQuery({
    queryKey: ["coach", "calculation_profile", activeAdhId],
    queryFn: () => fetchProfile(login, i18n, activeAdhId as number),
    enabled: !!client?.id && !!activeAdhId,
  });

  const {
    data: profiles,
  } = useQuery({
    queryKey: ["coach", "calculation_available_profiles"],
    queryFn: () => fetchAvailableProfiles(login, i18n),
    enabled: !!login?.id && !!is_calculated_diary,
  });

  const loading = !!clientLoading || (!!activeAdhId && !!profileLoading);

  const clientName = [client?.call_name || client?.first_name, client?.last_name].filter(fff => !!fff).join(' ');

  const mutationCoachSetAdh = useMutation({
    mutationFn: (pars: {
      adh_profile_id?: number;
      allow_client_edit?: boolean;
    }) => {
      let formData = new FormData();
      formData.append('object', 'adh_profile');
      formData.append('class_action', 'custom_api_coach_set_for_client');
      formData.append('client_id', (client?.id || 0).toString());
      formData.append('adh_profile_id', (pars.adh_profile_id || 0).toString());
      formData.append('allow_client_edit', (!!pars.allow_client_edit ? '1' : '0'));
      return fetch_one<{custom_result: {success: boolean, adh_profile_id?: number}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setSelectedProfileId(undefined);
      setDraftLinks(undefined);
      setEditing(false);
      queryClient.invalidateQueries({queryKey: ["coach", "calculation_client", id]});
      queryClient.invalidateQueries({queryKey: ["coach", "calculation_profile"]});
      queryClient.invalidateQueries({queryKey: ["calculation_w", "adh_profile"]});
      setSavedOpen(true);
    },
  });

  const mutationResetLinks = useMutation({
    mutationFn: () => {
      let formData = new FormData();
      formData.append('object', 'adh_profile');
      formData.append('class_action', 'custom_api_reset_client_links');
      formData.append('id', (profile?.id || 0).toString());
      return fetch_one<{custom_result: {success: boolean}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setDraftLinks(undefined);
      setEditing(false);
      queryClient.invalidateQueries({queryKey: ["coach", "calculation_profile"]});
      queryClient.invalidateQueries({queryKey: ["calculation_w", "adh_profile"]});
      setSavedOpen(true);
    },
  });

  const mutationUpdateLinks = useMutation({
    mutationFn: (pars: {
      links: AdhLink[];
    }) => {
      let formData = new FormData();
      formData.append('object', 'adh_profile');
      formData.append('class_action', 'custom_api_update_client_links');
      formData.append('id', (profile?.id || 0).toString());
      formData.append('name', draftName || '');
      pars.links.forEach((ll, i) => {
        formData.append(`links[${i}][nutritional_value_id]`, (ll.nutritional_value_id || 0).toString());
        formData.append(`links[${i}][is_tracked_client]`, (!!ll.is_tracked_client ? '1' : '0'));
        formData.append(`links[${i}][lower_limit]`, (ll.lower_limit ?? '').toString());
        formData.append(`links[${i}][higher_limit]`, (ll.higher_limit ?? '').toString());
        formData.append(`links[${i}][maximum]`, (ll.maximum ?? '').toString());
      });
      return fetch_one<{custom_result: {success: boolean}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        formData,
        login
      );
    },
    onSuccess: (data, variables, context) => {
      setDraftLinks(undefined);
      queryClient.invalidateQueries({queryKey: ["coach", "calculation_profile"]});
      queryClient.invalidateQueries({queryKey: ["calculation_w", "adh_profile"]});
      setSavedOpen(true);
    },
  });

  useEffect(() => {
    setAllowClientEdit(!consentAdhId);
  }, [consentAdhId]);

  useEffect(() => {
    setDraftLinks(undefined);
    setEditing(false);
  }, [profile?.id]);

  const sortedLinks = [...(profile?.adh_links || [])].sort(compareLinks);
  const visibleLinks = (!!draftLinks ? draftLinks : sortedLinks)
    .filter(ll => {
      if (!searchLink) return !!(ll.lower_limit || ll.higher_limit || ll.maximum) || !!ll.is_tracked_client || !!draftLinks;
      return (ll.nutritional_value?.name || '').toLowerCase().indexOf(searchLink.toLowerCase()) > -1;
    });

  const linksDirty = !!draftLinks && (
    draftName !== selectContentWithId(profile?.full_name, profile?.name) ||
    JSON.stringify(draftLinks.map((ll: any) => [ll.nutritional_value_id, !!ll.is_tracked_client, ll.lower_limit, ll.higher_limit, ll.maximum])) !== JSON.stringify(sortedLinks.map((ll: any) => [ll.nutritional_value_id, !!ll.is_tracked_client, ll.lower_limit, ll.higher_limit, ll.maximum]))
  );

  const currentStatus = !!consentAdhId
    ? t("coach.calculation.profile_coach")
    : isOwnChoice
      ? t("coach.calculation.profile_choice")
      : !!activeAdhId
        ? t("coach.calculation.profile_calculated")
        : t("coach.calculation.profile_none");

  const targetProfileId = selectedProfileId || consentAdhId || client?.adh_profile_id || client?.calculated_adh_profile_id;

  return <Page
    title={t("coach.calculation.title")}
    titleCrumbs={[
      {
        title: t("coach.menu.users", "Cliënten"),
        path: "/users"
      },
      {
        title: !!clientName ? clientName : 'Cliënt',
        path: `/user/${id}/detail`
      }
    ]}
  >
    <Card larger title={`${t("coach.calculation.title")}${!!clientName ? ` - ${clientName}` : ''}`}>
    <Grid container spacing={2} sx={{paddingBottom: 2}}>
      {loading && <Grid item xs={12}>
        <Typography>{t("general.loading", "Laden...")}</Typography>
      </Grid>}

      {!loading && !activeAdhId && <Grid item xs={12}>
        <Typography sx={{fontStyle: 'italic'}}>{t("coach.calculation.no_profile")}</Typography>
      </Grid>}

      {!loading && !!activeAdhId && !profile && <Grid item xs={12}>
        <Typography sx={{fontStyle: 'italic'}}>{t("coach.calculation.no_profile")}</Typography>
      </Grid>}

      {!loading && !!profile && <>
        <Grid item xs={12} sm={6} md={4}>
          <Typography sx={{fontSize: '0.7rem'}}>{t("coach.calculation.active_profile")}</Typography>
          <Typography>{currentStatus}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <Typography sx={{fontSize: '0.7rem'}}>{t("coach.calculation.title")}</Typography>
          <Typography>{selectContentWithId(profile.full_name, profile.name)}{!!describeProfile(profile, t) ? ` (${describeProfile(profile, t)})` : ''}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography sx={{fontSize: '0.7rem'}}>{t("coach.calculation.source")}</Typography>
          <Typography>{profile.source || '-'}{!!profile.source_date ? ` (${DateTime.fromISO(profile.source_date).toFormat("dd/LL/y")})` : ''}</Typography>
        </Grid>
        {!!profile.derived && <Grid item xs={12} sm={6} md={8}>
          <Typography sx={{fontSize: '0.7rem'}}>{t("coach.calculation.based_on")}</Typography>
          <Typography>{selectContentWithId(profile.derived.full_name, profile.derived.name)}</Typography>
        </Grid>}
      </>}

      <Grid item xs={12}>
        <Divider sx={{my: 1}} />
        <Typography variant="h6" sx={{paddingBottom: 1}}>{t("coach.calculation.profile_choice.title")}</Typography>
        <Select
          id="coach_adh_profile"
          caption={t("coach.calculation.profile_choice.caption")}
          backend
          fullWidth
          displayEmpty
          value={selectedProfileId || ''}
          setValue={(v) => {
            setSelectedProfileId(!!v ? parseInt(v.toString(), 10) : undefined);
          }}
        >
          <MenuItem key="profile_none" value={''}>{currentStatus}</MenuItem>
          {(profiles || []).map(pp => (
            <MenuItem key={`profile_${pp.id}`} value={pp.id}>
              {selectContentWithId(pp.full_name, pp.name)}
              {!!describeProfile(pp, t) && <span style={{opacity: 0.6, marginLeft: 6, fontSize: '0.8rem'}}>{describeProfile(pp, t)}</span>}
            </MenuItem>
          ))}
        </Select>
        <Switcher
          id="coach_allow_client_edit"
          backend
          caption={t("coach.calculation.allow_client_edit")}
          value={allowClientEdit}
          onChange={(c) => {
            setAllowClientEdit(!!c);
          }}
          sx={{marginTop: 2}}
        />
        {!allowClientEdit && !!targetProfileId && <Alert severity="info" sx={{marginTop: 2}}>
          {t("coach.calculation.coach_locked_hint")}
        </Alert>}
        {!!mutationCoachSetAdh.isPending && <CircularProgress sx={{marginTop: 2}} />}
        {!mutationCoachSetAdh.isPending && <Button
          id="coach_save_adh_profile"
          contained
          label={t("client.general.buttons.submit", "Opslaan")}
          disabled={!selectedProfileId && !consentAdhId}
          onClick={() => {
            mutationCoachSetAdh.mutate({
              adh_profile_id: selectedProfileId,
              allow_client_edit: allowClientEdit
            });
          }}
          sx={{marginTop: 2}}
        />}
      </Grid>

      {!loading && !!profile && !editing && <Grid item xs={12}>
        <Divider sx={{my: 1}} />
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Typography variant="h6" sx={{marginRight: 1.5}}>{t("client.calculation.profile_details.title")}</Typography>
          <Button
            size="small"
            id="coach_edit_adh_links"
            label={t("client.calculation.profile_details.edit")}
            onClick={() => {
              setDraftLinks([...(profile?.adh_links || [])].sort(compareLinks));
              setDraftName(selectContentWithId(profile?.full_name, profile?.name));
              setEditing(true);
            }}
            sx={{
              marginTop: 0
            }}
          />
        </Box>
        {(profile.adh_links || [] as any[]).filter((fff: any) => !!fff.nutritional_value?.is_client_enabled)
          .filter((ll: any) => !!(ll.lower_limit || ll.higher_limit || ll.maximum) || !!ll.is_tracked_client)
          .sort((a: any, b: any) => {
            let tracked = (!!b.is_tracked_client ? 1 : 0) - (!!a.is_tracked_client ? 1 : 0);
            if (tracked !== 0) return tracked;
            return compareLinks(a, b);
          })
          .map((ll: any) => (
            <Box key={`link_${ll.nutritional_value_id}`} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, flexWrap: 'wrap', gap: 1}}>
              <Typography sx={{fontWeight: 'bold'}}>{ll.nutritional_value?.name}</Typography>
              <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                {!!ll.lower_limit && !!ll.higher_limit && <Chip
                  size="small"
                  color="primary"
                  label={`${formatNumber(ll.lower_limit)} - ${formatNumber(ll.higher_limit)} ${limitUnit(ll)}`.trim()}
                />}
                {!!ll.lower_limit && !ll.higher_limit && <Chip
                  size="small"
                  label={`${t("client.calculation.limits.min")} ${formatNumber(ll.lower_limit)} ${limitUnit(ll)}`.trim()}
                />}
                {!ll.lower_limit && !!ll.higher_limit && <Chip
                  size="small"
                  label={`<= ${formatNumber(ll.higher_limit)} ${limitUnit(ll)}`.trim()}
                />}
                {!!ll.maximum && <Chip
                  size="small"
                  color="error"
                  label={`${t("client.calculation.limits.max")} ${formatNumber(ll.maximum)} ${limitUnit(ll)}`.trim()}
                />}
              </Box>
            </Box>
          ))}
      </Grid>}

      {!loading && !!profile && !!editing && <Grid item xs={12}>
        <Divider sx={{my: 1}} />
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Typography variant="h6">{t("client.calculation.profile_details.title")}</Typography>
          <Box sx={{flexGrow: 1}} />
          {!!mutationUpdateLinks.isPending || !!mutationResetLinks.isPending ? <CircularProgress /> : (<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Button
              id="coach_back_adh_links"
              label={t("client.calculation.profile_details.back")}
              onClick={() => {
                setDraftLinks(undefined);
                setEditing(false);
              }}
            />
            <Button
              id="coach_reset_adh_links"
              label={t("client.calculation.profile_details.reset")}
              disabled={!profile?.derived_id}
              startIcon={<RestoreIcon />}
              onClick={() => {
                setResetConfirmOpen(true);
              }}
            />
            <Button
              id="coach_save_adh_links"
              contained
              label={t("client.general.buttons.submit", "Opslaan")}
              disabled={!linksDirty}
              onClick={() => {
                mutationUpdateLinks.mutate({links: draftLinks || []});
              }}
            />
          </Box>)}
        </Box>
        <Grid container spacing={2} sx={{paddingTop: 1, paddingBottom: 1}}>
          <Grid item xs={12} sm={6} md={4}>
            <TextInput
              id="coach_own_profile_name"
              caption={t("client.calculation.profile_details.name")}
              value={draftName || ''}
              onChange={(v: string) => {setDraftName(v);}}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8} sx={{alignSelf: 'end'}}>
            <TextInput
              id="coach_search_link"
              value={searchLink}
              onChange={setSearchLink}
              caption={t("client.calculation.profile_details.search")}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
        {visibleLinks.filter(fff => !!fff.nutritional_value?.is_client_enabled).map((ll) => {
          const updateDraftLink = (changes: Partial<AdhLink>) => {
            let newLinks = [...(draftLinks || [])];
            const idx = newLinks.findIndex(nn => nn.nutritional_value_id === ll.nutritional_value_id);
            if (idx > -1) newLinks[idx] = {...newLinks[idx], ...changes};
            setDraftLinks(newLinks);
          };
          const toggleTracked = () => {
            updateDraftLink({is_tracked_client: !ll.is_tracked_client});
          };
          return (
          <Grid item xs={12} sm={6} md={4} key={`edit_link_${ll.nutritional_value_id}`}>
          <Box
            sx={{
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 2,
              padding: 1.5,
              height: '100%',
              opacity: !!ll.is_tracked_client ? 1 : 0.55
          }}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <Checkbox
                checked={!!ll.is_tracked_client}
                onChange={toggleTracked}
              />
              <Typography sx={{fontWeight: 'bold'}}>{ll.nutritional_value?.name}</Typography>
            </Box>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap'}}>
              <Box sx={{flex: 1, minWidth: '80px'}}>
                <NumberInput
                  id={`coach_limit_lower_${ll.nutritional_value_id}`}
                  caption={t("client.calculation.limits.lower")}
                  value={ll.lower_limit}
                  onChange={(n: number | undefined) => {
                    updateDraftLink({lower_limit: n});
                  }}
                  endIcon={<InputAdornment position="end">{limitUnit(ll)}</InputAdornment>}
                />
              </Box>
              <Box sx={{flex: 1, minWidth: '80px'}}>
                <NumberInput
                  id={`coach_limit_higher_${ll.nutritional_value_id}`}
                  caption={t("client.calculation.limits.recommended")}
                  value={ll.higher_limit}
                  onChange={(n: number | undefined) => {
                    updateDraftLink({higher_limit: n});
                  }}
                  endIcon={<InputAdornment position="end">{limitUnit(ll)}</InputAdornment>}
                />
              </Box>
              <Box sx={{flex: 1, minWidth: '80px'}}>
                <NumberInput
                  id={`coach_limit_maximum_${ll.nutritional_value_id}`}
                  caption={t("client.calculation.limits.maximum")}
                  value={ll.maximum}
                  onChange={(n: number | undefined) => {
                    updateDraftLink({maximum: n});
                  }}
                  endIcon={<InputAdornment position="end">{limitUnit(ll)}</InputAdornment>}
                />
              </Box>
            </Box>
          </Box>
          </Grid>
          );
        })}
        </Grid>
      </Grid>}
    </Grid>
    </Card>

    <Confirm
      open={resetConfirmOpen}
      setOpen={setResetConfirmOpen}
      title={t("client.calculation.profile_details.reset_confirm.title")}
      content={t("client.calculation.profile_details.reset_confirm.content")}
      execute={() => {
        setResetConfirmOpen(false);
        mutationResetLinks.mutate();
      }}
    />

    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />
  </Page>;
}
